import { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { CheckCircle2, XCircle, Loader2, Watch } from "lucide-react";

export default function WearableConnected() {
  const [sp] = useSearchParams();
  const navigate = useNavigate();
  const link = sp.get("link") || "";
  const owError = sp.get("error");
  const [athleteId, setAthleteId] = useState(sp.get("athlete_id") || "");
  const [athleteName, setAthleteName] = useState("");
  const [provider, setProvider] = useState(sp.get("provider") || "wearable");
  const [state, setState] = useState("working"); // working | done | error
  const [days, setDays] = useState(0);
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    if (owError) { setState("error"); return; }
    // The link token issued when the connection started tells us exactly whose watch this is.
    api.post(`/wearables/openwearables/confirm`, { link, athlete_id: athleteId, provider })
      .then((r) => {
        setDays(r.data.days_written || 0);
        if (r.data.athlete_id) setAthleteId(r.data.athlete_id);
        if (r.data.athlete_name) setAthleteName(r.data.athlete_name);
        if (r.data.provider) setProvider(r.data.provider);
        setState("done");
      })
      .catch(() => setState("error"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const back = () => navigate(athleteId ? `/athlete/${athleteId}?tab=training` : "/");
  // Once the data is in, take the coach straight back to the athlete they were working on.
  useEffect(() => {
    if (state !== "done" || !athleteId) return;
    const t = setTimeout(back, 3000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, athleteId]);

  const pretty = provider.charAt(0).toUpperCase() + provider.slice(1);

  return (
    <div className="min-h-[70vh] flex items-center justify-center p-8">
      <div className="border border-border rounded-2xl max-w-md w-full p-8 text-center" data-testid="wearable-connected-card">
        {state === "working" && (
          <>
            <Loader2 className="w-10 h-10 mx-auto animate-spin text-muted-foreground" />
            <h1 className="font-heading text-2xl font-black mt-4">Connecting {pretty}…</h1>
            <p className="text-sm text-muted-foreground mt-2">Confirming the link and pulling the first batch of data.</p>
          </>
        )}
        {state === "done" && (
          <>
            <CheckCircle2 className="w-12 h-12 mx-auto text-traffic-green" data-testid="wc-success-icon" />
            <h1 className="font-heading text-2xl font-black mt-4" data-testid="wc-success-title">{pretty} connected</h1>
            {athleteName && <p className="text-sm mt-1" data-testid="wc-athlete-name">Linked to <b>{athleteName}</b></p>}
            <p className="text-sm text-muted-foreground mt-2">
              {days > 0
                ? <>Pulled <b className="text-foreground">{days}</b> day(s) of history onto this athlete. New data keeps flowing in and recomputes automatically.</>
                : <>Link confirmed. Data usually takes a little while to arrive from {pretty} · it will appear on this athlete automatically once it lands.</>}
            </p>
            <button data-testid="wc-back-btn" onClick={back} className="mt-6 w-full bg-foreground text-background py-3 rounded-lg font-medium hover:bg-neutral-800 transition-colors flex items-center justify-center gap-2">
              <Watch className="w-4 h-4" /> Back to {athleteName || "Training"}
            </button>
            <p className="text-[11px] text-muted-foreground mt-2">Taking you back automatically…</p>
          </>
        )}
        {state === "error" && (
          <>
            <XCircle className="w-12 h-12 mx-auto text-traffic-red" data-testid="wc-error-icon" />
            <h1 className="font-heading text-2xl font-black mt-4">Connection not completed</h1>
            <p className="text-sm text-muted-foreground mt-2">
              We couldn't confirm the {pretty} connection{owError ? ` (${owError})` : ""}. Open the athlete and try again · nothing was saved.
            </p>
            <button data-testid="wc-back-btn" onClick={back} className="mt-6 w-full border border-foreground rounded-lg py-3 font-medium hover:bg-surface transition-colors">
              Back to the athlete
            </button>
          </>
        )}
      </div>
    </div>
  );
}
